<template>
    <div class="flex flex-col gap-4 min-h-0 overflow-auto scroll-thin p-4 sm:p-6 md:pl-0 md:-my-6 md:-mr-6">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-extrabold">New Message</div>
            <div>
                <vx-button
                    data-test="reset-button"
                    @click="reset()"
                    size="md"
                    color="muted-dark"
                    :disabled="resetDisabled"
                >
                    Reset
                    <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <vx-box v-if="currentOrgUnit" color="info" class="relative" :disabled="fromLocked">
            <div v-if="fromLocked" class="absolute w-full h-full flex items-center justify-center z-10 rounded-xl">
                <div class="m-auto">
                    <vx-alert color="slate" :closeable="false" shadow>
                        <template v-slot:icon>
                            <font-awesome-icon widthAuto :icon="faLock"></font-awesome-icon>
                        </template>
                        <strong>{{ data.from_group_name }}</strong> can't be changed after adding contacts.
                        <template v-slot:right>
                            <vx-button @click="reset()" size="sm" color="muted-dark" class="ml-3">
                                Reset Form
                                <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                            </vx-button>
                        </template>
                    </vx-alert>
                </div>
            </div>

            <div class="py-5 px-6" :class="{ 'opacity-30 bg-blend-multiply': fromLocked }">
                <vx-label size="lg" class="mb-2">
                    From
                    <template v-slot:description> Which Group do you want to send from? </template>
                </vx-label>
                <select-group
                    v-model="data.from_group_id"
                    :orgUnitId="currentOrgUnit.id"
                    @option:selected="data.from_group_name = $event.name"
                    size="l"
                    :clearable="false"
                ></select-group>
            </div>
        </vx-box>

        <vx-box color="info" class="relative" :disabled="fromRequired">
            <div v-if="fromRequired" class="absolute w-full h-full flex items-center justify-center z-10 rounded-xl">
                <div class="m-auto">
                    <vx-alert color="slate" :closeable="false" shadow>
                        Complete the <strong>From</strong> step before adding contacts.
                    </vx-alert>
                </div>
            </div>

            <div class="py-5 px-6" :class="{ 'opacity-30 bg-blend-multiply': fromRequired }">
                <vx-label size="lg" class="mb-2">
                    To
                    <template v-slot:description>
                        Search contacts by phone number. Add up to {{ PHONE_NUMBERS_LIMIT }} phone numbers (comma
                        separated).
                    </template>
                </vx-label>
                <contact-phone-number-selectable
                    v-model="data.contacts"
                    :disabled="fromRequired"
                    :groupId="resolvedGroupId"
                    :key="resolvedGroupId"
                ></contact-phone-number-selectable>
            </div>
        </vx-box>

        <vx-box color="info">
            <div class="py-5 px-6">
                <vx-label size="lg" class="mb-2">Message</vx-label>
                <message-builder
                    ref="messageBuilderRef"
                    placeholder="Enter Message"
                    v-model="data.body"
                    v-model:mediaUrl="data.media_url"
                    v-model:mediaContentType="data.media_content_type"
                    @selected:snippet="data.selected_snippet = $event.body"
                    @submit="sendMessage()"
                    :disableSchedule="!!currentOrgUnit"
                    @schedule="
                        data.send_at = $event;
                        sendMessage({ isScheduling: true });
                    "
                    @blur="updateDraft"
                    @update:mediaUrl="updateDraft"
                    @update:mediaContentType="updateDraft"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    preview
                ></message-builder>
            </div>
        </vx-box>

        <vx-sticky-footer class="max-w-(--breakpoint-sm) mx-auto sm:py-5 mt-4">
            <vx-button
                v-if="!currentOrgUnit"
                :loading="data.sending"
                @click.prevent="openScheduleModal()"
                color="muted"
                size="lg"
                type="button"
            >
                Schedule <font-awesome-icon widthAuto :icon="faCalendar"></font-awesome-icon>
            </vx-button>
            <vx-button
                data-test="send-button"
                :loading="data.sending"
                @click.prevent="sendMessage()"
                block
                color="primary"
                size="lg"
                type="button"
            >
                Send
                <font-awesome-icon widthAuto :icon="faPaperPlane"></font-awesome-icon>
            </vx-button>
        </vx-sticky-footer>
    </div>
</template>

<script setup>
import { faCalendar, faLock, faPaperPlane, faUndo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxLabel, VxStickyFooter } from '@voxie/frontend-components';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { displayContactName, formatPhoneNumber, pluralized } from '~/components/filters';
import SelectGroup from '~/components/general/SelectGroup.vue';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import ContactPhoneNumberSelectable from '~/components/message-hub/message-hub-new-conversation/ContactPhoneNumberSelectable.vue';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useMessageHubDrafts } from '~/components/message-hub/useMessageHubDrafts';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageHubRules } from '~/composables/useMessageHubRules';
import { useToasted } from '~/composables/useToasted';
import { actionsClient, messagesClient, scheduledMessagesClient } from '~/services';
import { getClientTz, roundToNearest5 } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { catchRequestError } from '~/utils/request';
import { displayNotActiveModal, isNotActive } from '~/utils/team';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const { checkPhoneNumber } = useMessageHub();
const { onCorporate, getCurrentGroupId, getContext, currentOrgUnit } = useCurrentGroup();
const { blockImages, blockFiles, getMessageHubRules } = useMessageHubRules();
const { saveDraft, clearDraft, getDraft } = useMessageHubDrafts();
const router = useRouter();
const route = useRoute();

const PHONE_NUMBERS_LIMIT = 20;

const messageBuilderRef = ref();

const data = reactive({
    body: '',
    media_url: null,
    media_content_type: null,
    selected_snippet: '',
    sending: false,
    scheduling: false,
    send_at: roundToNearest5(dayjs().tz(getClientTz()).add(1, 'h').utc()),
    from_group_id: null,
    from_group_name: null,
    contacts: [],
});

const resolvedGroupId = computed(() => data.from_group_id ?? getCurrentGroupId());

const fromRequired = computed(() => !!currentOrgUnit.value && !data.from_group_id);
const fromLocked = computed(() => data.contacts.length > 0);
const resetDisabled = computed(
    () => data.contacts.length === 0 && !data.body && !data.media_url && !data.from_group_id
);

onMounted(async () => {
    if (isNotActive()) {
        displayNotActiveModal();
        return;
    }

    checkPhoneNumber();

    const draft = getDraft(getContext(), 'new-conversation');

    if (draft) {
        data.body = draft.body || '';
        data.media_url = draft.media_url || null;
        data.media_content_type = draft.media_content_type || null;
        data.from_group_id = draft.from_group_id || null;
        data.from_group_name = draft.from_group_name || null;
    }
});

watch(
    resolvedGroupId,
    (groupId) => {
        getMessageHubRules(groupId);
    },
    { immediate: true }
);

const openScheduleModal = () => {
    messageBuilderRef.value.scheduleModal = true;
};

const validate = () => {
    const errors = [];

    if (currentOrgUnit.value && !data.from_group_id) {
        errors.push('Please select a group before sending a message.');
    }

    if (data.contacts.length === 0) {
        errors.push('Please add at least one contact.');
    } else if (data.contacts.length > PHONE_NUMBERS_LIMIT) {
        errors.push(`You cannot send a message to more than ${PHONE_NUMBERS_LIMIT} phone numbers.`);
    } else {
        data.contacts.forEach(({ phone, processing, invalid }) => {
            if (processing) {
                errors.push(`Please wait, the phone number ${formatPhoneNumber(phone)} is still being processed.`);
            } else if (invalid) {
                errors.push(invalid);
            }
        });
    }

    if (!data.body || data.body.trim() === '') {
        errors.push('Message body is required.');
    }

    if (messageBuilderRef.value?.uploadingMedia) {
        errors.push('Please wait, the attachment is still uploading.');
    }

    if (data.scheduling) {
        if (!data.send_at) {
            errors.push('Send At is required.');
        }

        if (
            data.send_at !== null &&
            dayjs().tz(getClientTz()).utc().isSameOrAfter(dayjs.utc(data.send_at).tz(getClientTz()))
        ) {
            errors.push('The send at field cannot be set in the past.');
        }
    }

    if (errors.length !== 0) {
        errors.forEach((error) => {
            toasted.error(error);
        });

        return false;
    }

    return true;
};

const deliverMessage = async (contact) => {
    const payload = {
        contact_id: contact.id,
        body: data.body,
    };

    if (data.scheduling) {
        payload.send_at = data.send_at;
    }

    if (data.media_url) {
        payload.media_url = data.media_url;
        payload.media_content_type = data.media_content_type;
    }

    try {
        if (data.scheduling) {
            await scheduledMessagesClient.createScheduledMessage(
                { teamId, groupId: resolvedGroupId.value },
                contact.id,
                payload
            );
        } else {
            if (onCorporate() && !data.from_group_id) {
                await messagesClient.createV3(teamId, payload);
            } else {
                await messagesClient.groupMessageCreate(teamId, resolvedGroupId.value, payload);
            }
        }

        return contact;
    } catch (error) {
        throw new Error(`Failed to send to ${displayContactName(contact)}: ${catchRequestError(error)}`, {
            cause: error,
        });
    }
};

const sendMessage = (event) => {
    data.scheduling = event ? event.isScheduling : false;

    if (isNotActive()) {
        displayNotActiveModal();
        return;
    }

    if (data.sending) {
        return;
    }

    if (!validate()) {
        return;
    }

    data.sending = true;

    Promise.allSettled(data.contacts.map((contact) => deliverMessage(contact)))
        .then((results) => {
            const successes = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
            const failures = results
                .filter((result) => result.status !== 'fulfilled')
                .map((result) => result.reason.message);

            failures.forEach((failure) => toasted.error(failure));

            if (successes.length >= 1) {
                reset();
            }

            if (failures.length && successes.length) {
                toasted.success(
                    `${pluralized('message', successes.length, true)} ${
                        data.scheduling ? 'scheduled' : 'sent'
                    } successfully`
                );
            } else if (!failures.length && successes.length === 1) {
                router.push({
                    name: 'message-hub.conversation',
                    params: {
                        contactId: successes[0].id,
                    },
                    query: {
                        ...route.query,
                        group: currentOrgUnit.value && resolvedGroupId.value ? resolvedGroupId.value : undefined,
                    },
                });
            }

            actionsClient.send('message_sent');

            if (data.selected_snippet) {
                actionsClient.send('snippet_used');
            }

            clearDraft(getContext(), 'new-conversation');
        })
        .finally(() => {
            data.sending = false;
        });
};

const reset = () => {
    messageBuilderRef.value?.cancelPaste();
    data.body = '';
    data.media_url = null;
    data.media_content_type = null;
    data.selected_snippet = '';
    data.scheduling = false;
    data.from_group_id = null;
    data.from_group_name = null;
    data.contacts = [];
    clearDraft(getContext(), 'new-conversation');
};

const updateDraft = () => {
    if (!data.body.trim()) {
        clearDraft(getContext(), 'new-conversation');
        return;
    }

    saveDraft(getContext(), 'new-conversation', {
        body: data.body,
        media_url: data.media_url,
        media_content_type: data.media_content_type,
        from_group_id: data.from_group_id,
        from_group_name: data.from_group_name,
    });
};

defineExpose({
    validate,
});
</script>
